import { FunctionComponent, useEffect, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiBank,
  mdiMapMarker,
  mdiAccount,
  mdiAlertCircleOutline,
} from "@mdi/js";
import { Country } from "country-state-city";
import ModalDialog from "../../../../ui/new_components/common/modal";
import {
  CustomerBillingAddressModel,
  CustomerSpocModel,
} from "../../../../context_providers/auth/user_model";
import { stateData } from "../../../../utils/states_data";
import RequiredMark from "../../../../ui/new_components/common/required_mark";

export interface AddrModalState {
  spocId: string;
  addrIndex: number | null;
}

interface Props {
  spocs: CustomerSpocModel[];
  onChange: (spocs: CustomerSpocModel[]) => void;
  selectedSpocId?: string;
  addrModal: AddrModalState | null;
  onAddrModalChange: (m: AddrModalState | null) => void;
  onDeleteAddress?: (spocId: string, addr: CustomerBillingAddressModel) => void;
  viewOnly?: boolean;
  id_prefix?: string;
}

const emptyAddress = (): CustomerBillingAddressModel => ({
  branch_name: "",
  type: "Delivery",
  line1: "",
  line2: "",
  line3: "",
  city: "",
  state: "",
  country: "India",
  pincode: 0,
});

type CountryType = { isoCode: string; name: string };

const SpocDeliveryAddresses: FunctionComponent<Props> = ({
  spocs,
  onChange,
  addrModal,
  onAddrModalChange,
  onDeleteAddress,
  viewOnly = false,
  id_prefix = "spoc-delivery",
}) => {
  const [draft, setDraft] =
    useState<CustomerBillingAddressModel>(emptyAddress());
  const [error, setError] = useState("");
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [toDelete, setToDelete] = useState<{
    spocId: string;
    spocName: string;
    addr: CustomerBillingAddressModel;
  } | null>(null);

  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  const isEdit = addrModal?.addrIndex != null;

  useEffect(() => {
    if (!addrModal) return;
    const spoc = spocs.find((s) => s.id === addrModal.spocId);
    if (addrModal.addrIndex !== null && spoc) {
      setDraft({ ...spoc.delivery_addresses[addrModal.addrIndex] });
    } else {
      setDraft(emptyAddress());
    }
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addrModal]);

  const patchDraft = (patch: Partial<CustomerBillingAddressModel>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const save = () => {
    if (!addrModal) return;
    const missing: string[] = [];
    if (!draft.branch_name?.trim()) missing.push("Branch Name");
    if (!draft.line1?.trim()) missing.push("Address Line 1");
    if (!draft.city?.trim()) missing.push("City");
    if (!draft.state?.trim()) missing.push("State");
    if (!draft.country?.trim()) missing.push("Country");
    if (
      draft.country === "India" &&
      (!draft.pincode || String(draft.pincode).length !== 6)
    ) {
      missing.push("Pincode (6 digits)");
    }
    if (missing.length) {
      setError(`Please fill: ${missing.join(", ")}`);
      return;
    }
    onChange(
      spocs.map((s) => {
        if (s.id !== addrModal.spocId) return s;
        const list = s.delivery_addresses || [];
        const next =
          addrModal.addrIndex === null
            ? [...list, draft]
            : list.map((a, i) => (i === addrModal.addrIndex ? draft : a));
        return { ...s, delivery_addresses: next };
      }),
    );
    onAddrModalChange(null);
  };

  const renderCard = (
    addr: CustomerBillingAddressModel,
    ai: number,
    spoc: CustomerSpocModel,
  ) => (
    <div
      key={`${spoc.id}-${ai}`}
      id={`${id_prefix}-card-${spoc.id}-${ai}`}
      className="bg-white w-full p-5 rounded-lg border text-xs relative"
    >
      {!viewOnly && (
        <div className="absolute top-2 right-2 flex items-center gap-3">
          <button
            id={`${id_prefix}-card-${spoc.id}-${ai}-edit-btn`}
            type="button"
            onClick={() =>
              onAddrModalChange({
                spocId: spoc.id,
                addrIndex: ai,
              })
            }
            className="text-xs text-myPrimaryColor font-semibold underline"
          >
            Edit
          </button>
          <button
            id={`${id_prefix}-card-${spoc.id}-${ai}-delete-btn`}
            type="button"
            onClick={() =>
              setToDelete({
                spocId: spoc.id,
                spocName: spoc.name_of_contact_person || "",
                addr,
              })
            }
            className="text-xs text-red-500 font-semibold underline"
          >
            Delete
          </button>
        </div>
      )}
      <div className="flex gap-2 my-1">
        <span>
          <Icon path={mdiAccount} className="h-4 w-4 text-gray-500" />
        </span>
        <p>
          <span className="font-semibold">Contact: </span>
          <span>{spoc.name_of_contact_person || "--"}</span>
        </p>
      </div>
      <div className="flex gap-2 my-1">
        <span>
          <Icon path={mdiBank} className="h-4 w-4 text-gray-500" />
        </span>
        <p>
          <span className="font-semibold">Branch Name: </span>
          <span>{addr.branch_name || "--"}</span>
        </p>
      </div>
      <hr className="my-2" />
      <div className="flex gap-1">
        <span className="font-semibold"> Country :</span>
        <p>{addr.country ?? "--"}</p>
      </div>
      <div>
        <span className="font-semibold"> State : </span>
        <span>{addr.state ?? "--"}</span>
      </div>
      <div>
        <span className="font-semibold"> City : </span>
        <span>{addr.city ?? "--"}</span>
      </div>
      <hr className="my-2" />
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Icon path={mdiMapMarker} className="h-4 w-4 text-gray-500" />
          <span className="font-semibold text-balck">Address</span>
        </div>
        <div className="text-gray-500 text-xs">
          {[
            addr.line1,
            addr.line2,
            addr.line3,
            addr.city,
            addr.state,
            addr.country,
          ]
            .filter(Boolean)
            .join(", ")}{" "}
          - {addr.pincode}
        </div>
      </div>
    </div>
  );

  if (!spocs.length) {
    return (
      <p className="text-sm text-gray-400">
        Add a contact above to view and add its delivery address.
      </p>
    );
  }

  return (
    <>
      {/* Section-level "Add new" is intentionally disabled for now. A delivery
          address is added per-contact from the "Add Delivery Address" button on
          each contact card in the Contact Details section. */}

      {spocs.every((s) => (s.delivery_addresses?.length || 0) === 0) && (
        <p className="text-xs text-red-400">No delivery address added yet.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5">
        {spocs.flatMap((spoc) =>
          (spoc.delivery_addresses || []).map((addr, ai) =>
            renderCard(addr, ai, spoc),
          ),
        )}
      </div>

      {toDelete && (
        <ModalDialog
          id={`${id_prefix}-delete-modal`}
          title="Delete Delivery Address"
          show={!!toDelete}
          onClose={() => setToDelete(null)}
        >
          <div className="p-4 flex flex-col gap-4">
            <div className="flex gap-3">
              <Icon
                path={mdiAlertCircleOutline}
                size={1.3}
                className="text-red-500 shrink-0"
              />
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">
                  Are you sure you want to delete this delivery address?
                </p>
                <p>
                  Delivery address{" "}
                  <span className="font-semibold">
                    {toDelete.addr.branch_name || "—"}
                  </span>
                  {toDelete.spocName ? (
                    <>
                      {" "}
                      for contact{" "}
                      <span className="font-semibold">{toDelete.spocName}</span>
                    </>
                  ) : null}{" "}
                  will be permanently deleted. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                id={`${id_prefix}-delete-cancel-btn`}
                type="button"
                onClick={() => setToDelete(null)}
                className="px-3 py-1 border rounded text-sm"
              >
                Cancel
              </button>
              <button
                id={`${id_prefix}-delete-confirm-btn`}
                type="button"
                onClick={() => {
                  onDeleteAddress?.(toDelete.spocId, toDelete.addr);
                  setToDelete(null);
                }}
                className="px-3 py-1 bg-red-500 text-white rounded text-sm font-semibold"
              >
                Delete Address
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {addrModal && (
        <ModalDialog
          id={`${id_prefix}-editor-modal`}
          title={isEdit ? "Edit Delivery Address" : "Add Delivery Address"}
          show={!!addrModal}
          onClose={() => onAddrModalChange(null)}
        >
          <div className="p-3 flex flex-col gap-4 text-sm">
            <div className="flex gap-2 items-center">
              <label htmlFor={`${id_prefix}-branch`}>Branch</label>
              <input
                id={`${id_prefix}-branch`}
                className="w-1/2 p-2 rounded border focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
                type="text"
                placeholder="branch"
                value={draft.branch_name ?? ""}
                onChange={(e) => patchDraft({ branch_name: e.target.value })}
              />
            </div>

            <section className="w-full py-2 md:py-4 bg-white rounded-md p-2 md:p-4 shadow-md border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-6">
                {/* Left column */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-line1`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Address <RequiredMark />
                    </label>
                    <input
                      id={`${id_prefix}-line1`}
                      type="text"
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      value={draft.line1 ?? ""}
                      onChange={(e) => patchDraft({ line1: e.target.value })}
                      placeholder="Line 1"
                    />
                  </div>
                  <input
                    id={`${id_prefix}-line2`}
                    type="text"
                    className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={draft.line2 ?? ""}
                    onChange={(e) => patchDraft({ line2: e.target.value })}
                    placeholder="Line 2"
                  />
                  <input
                    id={`${id_prefix}-line3`}
                    type="text"
                    className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={draft.line3 ?? ""}
                    onChange={(e) => patchDraft({ line3: e.target.value })}
                    placeholder="Line 3"
                  />
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-country`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Country <RequiredMark />
                    </label>
                    <select
                      id={`${id_prefix}-country`}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      value={draft.country || ""}
                      onChange={(e) =>
                        patchDraft({ country: e.target.value, state: "" })
                      }
                    >
                      <option value="">Select Country</option>
                      {countries.map((c) => (
                        <option key={c.isoCode} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right column */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-state`}
                      className="text-sm font-medium text-gray-700"
                    >
                      State <RequiredMark />
                    </label>
                    {draft.country === "India" ? (
                      <select
                        id={`${id_prefix}-state`}
                        className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        value={draft.state ?? ""}
                        onChange={(e) => patchDraft({ state: e.target.value })}
                      >
                        <option value="">Select State</option>
                        {stateData.map((s) => (
                          <option key={s.id} value={s.name}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={`${id_prefix}-state`}
                        type="text"
                        className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        value={draft.state ?? ""}
                        onChange={(e) => patchDraft({ state: e.target.value })}
                        placeholder="State"
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-city`}
                      className="text-sm font-medium text-gray-700"
                    >
                      City <RequiredMark />
                    </label>
                    <input
                      id={`${id_prefix}-city`}
                      type="text"
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      value={draft.city ?? ""}
                      onChange={(e) => patchDraft({ city: e.target.value })}
                      placeholder="City"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-pincode`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Postal Code{" "}
                      {draft.country === "India" && <RequiredMark />}
                    </label>
                    <input
                      id={`${id_prefix}-pincode`}
                      type="number"
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      value={draft.pincode || ""}
                      onChange={(e) =>
                        patchDraft({ pincode: Number(e.target.value) || 0 })
                      }
                      placeholder="Postal Code"
                    />
                  </div>
                </div>
              </div>
            </section>

            {error && (
              <p
                id={`${id_prefix}-editor-error`}
                className="text-xs text-red-500"
              >
                {error}
              </p>
            )}
            <div className="flex justify-center">
              <button
                id={`${id_prefix}-editor-submit-btn`}
                type="button"
                onClick={save}
                className="bg-myPrimaryColor font-semibold text-sm text-white rounded-3xl px-4 py-1 hover:scale-110 transform duration-300"
              >
                {isEdit ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
    </>
  );
};

export default SpocDeliveryAddresses;
